import React, { useEffect, useState } from "react";
import { Button } from "@chemaxon/design-system/button";
import { Typography } from "@chemaxon/design-system/typography";
import ViewContainer from "@chemaxon/terminus-client/ui/components/view-container/ViewContainer";
import ContainerWithLoaderAndError from "@chemaxon/terminus-client/ui/components/container-with-loader-and-error/ContainerWithLoaderAndError";

import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import Tooltip from "@mui/material/Tooltip";
import Subheader from "@chemaxon/terminus-client/ui/components/subheader/Subheader";

import "@chemaxon/terminus-client/ui/views/projects/list/ListProjectsView.scss";
import { OnlyForTeamAdmin, Restricted } from "@chemaxon/terminus-client/ui/utils/Restricted";
import { Group } from "@chemaxon/terminus-client/ui/models/Group";
import {
  useGetGroups,
  useRemoveGroup,
} from "@chemaxon/terminus-client/ui/services/GroupService";
import MoreMenu from "@chemaxon/terminus-client/ui/components/more-menu/MoreMenu";
import RemoveButton from "@chemaxon/terminus-client/ui/components/more-menu/RemoveButton";
import ManageButton from "@chemaxon/terminus-client/ui/components/more-menu/ManageButton";
import SynergyMUIDataTableSearchBox from "@chemaxon/terminus-client/ui/components/mui-datatables-search-box/SynergyMUIDataTableSearchBox";
import CheckBoxConfirmDialog from "@chemaxon/terminus-client/ui/components/confirmation/CheckBoxConfirmDialog";
import AddGroup from "@chemaxon/terminus-client/ui/views/groups/add/AddGroup";
import EditGroup from "@chemaxon/terminus-client/ui/views/groups/edit/EditGroup";
import EditOff from "@mui/icons-material/EditOff";
import { adaptUseQueryResponseForContainerWithLoader } from "@chemaxon/terminus-client/ui/utils/queryHelpers";
import { useGetCurrentTeam } from "@chemaxon/terminus-client/ui/services/UserInfoService";
import DetailsButton from "@chemaxon/terminus-client/ui/components/more-menu/DetailsButton";
import {
  PlatformDomainObjectType,
  PlatformPermission,
} from "@chemaxon/terminus-client/ui/models/Permission";
import { ConditionalMessage } from "@chemaxon/terminus-client/ui/components/alerts/ConditionalMessage";
import { isExternalGroupManagement } from "@chemaxon/terminus-client/ui/models/Team";

function ListGroupView() {
  const getGroupsQueryResult = useGetGroups();
  const currentTeam = useGetCurrentTeam();
  const mutateRemoveGroup = useRemoveGroup();
  const [groupToRemove, setGroupToRemove] = useState<null | Group>(null);
  const [groupToEdit, setGroupToEdit] = useState<null | Group>(null);
  const [isInAddGroupState, setAddGroupState] = useState<boolean>(false);

  useEffect(() => {
    if (mutateRemoveGroup.isSuccess) {
      hideConfirmDialog();
    }
  }, [mutateRemoveGroup.isSuccess]);

  const groups =
    adaptUseQueryResponseForContainerWithLoader(getGroupsQueryResult);

  const hideConfirmDialog = () => {
    setGroupToRemove(null);
  };

  const hideCreateGroupDialog = () => {
    setAddGroupState(false);
  };

  const hideEditGroupDialog = () => {
    setGroupToEdit(null);
  };

  const createGroupButton = () => (
    <Restricted
      to={{
        platformDomainObjectType: PlatformDomainObjectType.GROUP,
        platformPermission: PlatformPermission.CREATE,
      }}
    >
      <Button
        colorVariant="primary"
        id="addGroupButton"
        onClick={() => setAddGroupState(true)}
      >
        Create new group
      </Button>
    </Restricted>
  );

  const getMuiTableOptions = (): MUIDataTableOptions => {
    return {
      customSearchRender: (searchText, handleSearch, hideSearch, options) => {
        return (
          <SynergyMUIDataTableSearchBox
            searchText={searchText}
            onSearch={handleSearch}
            onHide={hideSearch}
            options={options}
          />
        );
      },
      filterType: "dropdown",
      searchOpen: true,
      download: false,
      print: false,
      selectableRows: "none",
      elevation: 0,
      filter: false,
      viewColumns: false,
      sortOrder: {
        name: "ID",
        direction: "asc",
      },
      customSearch: (rawSearchQuery, currentRow, columns) => {
        const caseSensitive = false;
        let isFound = false;
        const searchQuery = caseSensitive
          ? rawSearchQuery
          : rawSearchQuery.toLowerCase();
        currentRow.forEach((col, i) => {
          if (!columns[i].searchable) {
            return;
          }

          let textInRow = i === 0 ? col["groupName"] : col.toString();
          if (!caseSensitive) {
            textInRow = textInRow.toString().toLowerCase();
          }

          if (textInRow.toString().indexOf(searchQuery) >= 0) {
            isFound = true;
          }
        });
        return isFound;
      },
    };
  };

  const getTableColumns = () => {
    const columns = [];

    columns.push({
      name: "Group name",
      options: {
        filter: true,
        sort: true,
        searchable: true,
        setCellHeaderProps: () => {
          return {
            style: {
              width: 320,
            },
          };
        },
        customBodyRender: renderGroupName,
      },
    });
    columns.push({
      name: "Members",
      options: {
        filter: true,
        sort: true,
        searchable: false,
        customBodyRender: renderGroupSize,
      },
    });
    columns.push({
      name: "Auto-assign",
      options: {
        filter: true,
        sort: true,
        searchable: false,
        setCellHeaderProps: () => {
          return {
            style: {
              width: 220,
            },
          };
        },
        customBodyRender: renderAutoAssign,
      },
    });
    columns.push({
      name: "",
      options: {
        filter: false,
        sort: false,
        searchable: false,
        customBodyRender: renderDropdownMenu,
      },
    });

    return columns;
  };

  const getTableData = (groups: Group[]): DataRow[] =>
    groups.map(group => [
      { groupName: group.groupName, isAdminGroup: group.isTeamAdminGroup },
      group.memberIds.length,
      group.autoAssign,
      group,
    ]);

  const renderDropdownMenu = (group: Group) => (
    <OnlyForTeamAdmin>
      <MoreMenu
        id={group.id}
        options={[
          {
            key: group.id + "_edit",
            option: !isExternalGroupManagement(currentTeam.data?.userAndGroupManagementMode) ? (
              <ManageButton onClick={() => editGroup(group)} />
            ) : (
              <DetailsButton onClick={() => editGroup(group)} />
            ),
            loading: false,
          },
          {
            key: group.id + "_remove",
            option: !group.isTeamAdminGroup && !isExternalGroupManagement(currentTeam.data?.userAndGroupManagementMode) && (
              <RemoveButton onClick={() => setGroupToRemove(group)} />
            ),
            loading: false,
          },
        ]}
      />
    </OnlyForTeamAdmin>
  );
  const editGroup = (group: Group) => {
    setGroupToEdit(group);
  };

  const renderGroupName = (data: NameColumnData) => {
    return (
      <>
        <div style={{ verticalAlign: "middle", display: "flex" }}>
          {renderName(data.groupName, 40)}
          {data.isAdminGroup ? (
            <EditOff fontSize={"small"} sx={{ marginLeft: 3 }} />
          ) : null}
        </div>
        {data.isAdminGroup ? (
          <Typography fontStyle="italic" color="grey" variant="label3">
            Formerly called SUPER_ADMIN
          </Typography>
        ) : null}
      </>
    );
  };

  const renderGroupSize = (size: number) => {
    return renderName(size + " members", 15);
  };

  const renderAutoAssign = (autoAssign: boolean) => {
    return renderName(autoAssign ? "on " : "off", 5);
  };

  const renderName = (name: string, charLength: number) => {
    if (name && name.length > charLength) {
      const truncName = name.substring(0, charLength - 1) + "...";

      return (
        <Tooltip title={name}>
          <span>{truncName}</span>
        </Tooltip>
      );
    }
    return <span>{name}</span>;
  };

  return (
    <React.Fragment>
      <OnlyForTeamAdmin>
        <Subheader title={"Groups"} button={createGroupButton()} />
        <ConditionalMessage condition={isExternalGroupManagement(currentTeam.data?.userAndGroupManagementMode)}>
            Group edit and delete operations are disabled because user and group
            membership management in this team is configured to be done from an
            external IdP.
          </ConditionalMessage>

        <ViewContainer id={"listGroupsContainer"} sx={{ mt: 33 }}>
          <ContainerWithLoaderAndError
            data={groups}
            content={(groups: Group[]) => {
              const tableColumns = getTableColumns();
              const tableData = getTableData(groups);
              return (
                <div className="listGroupsView">
                  <MUIDataTable
                    title={""}
                    columns={tableColumns}
                    data={tableData}
                    options={getMuiTableOptions()}
                  />
                  <CheckBoxConfirmDialog
                    isShown={groupToRemove !== null}
                    title={`Delete ${
                      groupToRemove ? groupToRemove.groupName : null
                    } group?`}
                    checkBoxLabel="I understand that deleting group is permanent and cannot be undone."
                    confirmButtonLabel={"Yes, delete it"}
                    submit={() =>
                      groupToRemove &&
                      mutateRemoveGroup.mutate(groupToRemove.id)
                    }
                    cancel={() => hideConfirmDialog()}
                    destructive={true}
                    loading={mutateRemoveGroup.isPending}
                    divider={false}
                  />
                </div>
              );
            }}
          />
        </ViewContainer>
        <AddGroup
          onCancel={() => hideCreateGroupDialog()}
          isShown={isInAddGroupState}
        />
        <EditGroup
          onCancel={() => hideEditGroupDialog()}
          isShown={groupToEdit != null}
          group={groupToEdit}
          scim={isExternalGroupManagement(currentTeam.data?.userAndGroupManagementMode)}
        />
      </OnlyForTeamAdmin>
    </React.Fragment>
  );
}

type DataRow = [
  nameColumnData: NameColumnData,
  groupSize: number,
  autoAssign: boolean,
  group: Group,
];

type NameColumnData = {
  groupName: string;
  isAdminGroup: boolean;
};

export default ListGroupView;
